import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { getTopicsForLevel, getTopicName } from '../data/topicCatalog';
import { lessonTotal, lessonsDone, levelProgress } from '../data/topicLessons';
import ProfileMenu from '../ProfileMenu';
import { languageLabel, languageFlag } from '../data/languageNames';
import './Home.css';

const NEXT_LEVEL = { A1: 'A2', A2: 'B1', B1: 'B2', B2: 'C1', C1: 'C2' };

function dayKey(date) {
    return date.toLocaleDateString('en-CA');
}

// Days in a row with at least one finished lesson. Today does not need to be done yet to keep the streak.
function calcStreak(isoDates) {
    const days = new Set(isoDates.filter(Boolean).map(d => dayKey(new Date(d))));
    const cursor = new Date();
    if (!days.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
    let streak = 0;
    while (days.has(dayKey(cursor))) {
        streak += 1;
        cursor.setDate(cursor.getDate() - 1);
    }
    return streak;
}

function readDismissed(key) {
    try { return localStorage.getItem(key) === '1'; } catch (e) { return false; }
}

const levelUpText = {
    en: {
        title: 'Level complete!',
        body: 'You finished every lesson at {level}. Ready for {next}?',
        up: 'Move up to {next}',
        later: 'Not yet',
        saving: 'Saving...',
    },
    it: {
        title: 'Livello completato!',
        body: 'Hai finito tutte le lezioni del livello {level}. Pronto per il {next}?',
        up: 'Passa al livello {next}',
        later: 'Non ora',
        saving: 'Salvataggio...',
    },
    es: {
        title: '¡Nivel completado!',
        body: 'Terminaste todas las lecciones del nivel {level}. ¿Listo para el {next}?',
        up: 'Subir al nivel {next}',
        later: 'Ahora no',
        saving: 'Guardando...',
    },
    fr: {
        title: 'Niveau terminé !',
        body: 'Tu as terminé toutes les leçons du niveau {level}. Prêt pour le {next} ?',
        up: 'Passer au niveau {next}',
        later: 'Pas maintenant',
        saving: 'Enregistrement...',
    },
    pt: {
        title: 'Nível concluído!',
        body: 'Você terminou todas as lições do nível {level}. Pronto para o {next}?',
        up: 'Subir para o nível {next}',
        later: 'Agora não',
        saving: 'Salvando...',
    },
};

function Home() {
    const navigate = useNavigate();
    const { t, interfaceLanguage } = useLanguage();
    const { activeLanguage, userLanguages, switchLanguage, updateActiveLevel } = useUserLanguage();
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [progressRows, setProgressRows] = useState([]);
    const [activityDates, setActivityDates] = useState([]);
    const [totalXp, setTotalXp] = useState(0);
    const [levelUpSaving, setLevelUpSaving] = useState(false);
    const [, setLevelUpTick] = useState(0);

    useEffect(() => {
        const getUser = async () => {
            const { data: { session } } = await supabase.auth.getSession();

            if (!session) {
                navigate('/signin');
                return;
            }

            const user = session.user;

            setUser(user);

            const { data: profileData } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();

            const onboardingStep = localStorage.getItem('vozoly_onboarding_step');
            if (!profileData || !profileData.target_language) {
                if (onboardingStep) {
                    navigate(`/onboarding/${onboardingStep}`);
                } else {
                    navigate('/language');
                }
                return;
            }

            setProfile(profileData);
            localStorage.setItem('vozoly_display_name', profileData.display_name || '');
            // Only fill these in if nothing is saved yet. The language context keeps them up to date after that.
            if (!localStorage.getItem('vozoly_level')) {
                localStorage.setItem('vozoly_level', profileData.level || 'A1');
            }
            if (!localStorage.getItem('vozoly_target_language')) {
                localStorage.setItem('vozoly_target_language', profileData.target_language || 'italian');
            }
            setLoading(false);
        };

        getUser();
    }, [navigate]);

    // Keep the saved language and level in step with the language the user has selected
    useEffect(() => {
        if (!activeLanguage) return;
        localStorage.setItem('vozoly_target_language', activeLanguage);
        const activeLevel = userLanguages.find(l => l.target_language === activeLanguage)?.level;
        if (activeLevel) localStorage.setItem('vozoly_level', activeLevel);
    }, [activeLanguage, userLanguages]);

    // Progress, XP and streak for the signed-in user
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const loadStats = async () => {
            const [progressRes, resultsRes, statsRes] = await Promise.all([
                supabase.from('user_progress').select('topic_slug, lesson_slug, completed_at, target_language').eq('user_id', user.id),
                supabase.from('lesson_results').select('completed_at').eq('user_id', user.id),
                supabase.from('user_stats').select('total_xp').eq('user_id', user.id).maybeSingle(),
            ]);
            if (cancelled) return;
            const rows = progressRes.data || [];
            setProgressRows(rows);
            setActivityDates([
                ...rows.map(r => r.completed_at),
                ...(resultsRes.data || []).map(r => r.completed_at),
            ]);
            setTotalXp(statsRes.data?.total_xp || 0);
        };
        loadStats();
        return () => { cancelled = true; };
    }, [user]);

    if (loading) {
        return (
            <div className="home-loading">
                <div className="home-loading-spinner"></div>
                <div className="home-loading-text">{t('common.loading')}</div>
            </div>
        );
    }

    const targetLang = activeLanguage || profile?.target_language || 'italian';
    const level = userLanguages.find(l => l.target_language === activeLanguage)?.level || profile?.level || 'A1';

    // Every language shares the same topic list, so the topics only depend on the user's level
    const topics = getTopicsForLevel(level).slice(0, 6);

    // Completed lessons for the language being learned
    const completedByTopic = {};
    progressRows
        .filter(r => (r.target_language || 'italian').toLowerCase() === targetLang.toLowerCase())
        .forEach(r => {
            if (!completedByTopic[r.topic_slug]) completedByTopic[r.topic_slug] = [];
            completedByTopic[r.topic_slug].push(r.lesson_slug);
        });

    const { done: levelDone, total: levelTotal, percent: levelPct } = levelProgress(level, completedByTopic);
    const streak = calcStreak(activityDates);

    const nextLevel = NEXT_LEVEL[level];
    const dismissKey = `vozoly_levelup_dismissed_${targetLang.toLowerCase()}_${level}`;
    const showLevelUp = !!nextLevel && levelTotal > 0 && levelDone >= levelTotal && !readDismissed(dismissKey);
    const lu = (key) => ((levelUpText[interfaceLanguage] || levelUpText.en)[key] || levelUpText.en[key])
        .replace('{level}', level)
        .replace('{next}', nextLevel || '');

    const handleLevelUp = async () => {
        if (!user || !nextLevel || levelUpSaving) return;
        setLevelUpSaving(true);
        await supabase.from('profiles').update({ level: nextLevel }).eq('id', user.id);
        await updateActiveLevel(nextLevel);
        setLevelUpSaving(false);
    };

    const dismissLevelUp = () => {
        try { localStorage.setItem(dismissKey, '1'); } catch (e) { }
        setLevelUpTick(n => n + 1);
    };


    const firstName = profile?.display_name || user?.email?.split('@')[0] || 'there';

    return (
        <div className="home-screen">

            <div className="home-top-bar">
                <div className="home-top-left">
                    <div className="home-greeting">{t('home.whatToExplore')}</div>
                    <div className="home-title">{t('home.greeting')}, {firstName} 👋</div>
                </div>
                <div className="home-top-right">
                    <div className="home-lang-pill">
                        {languageFlag(targetLang)} {languageLabel(targetLang, interfaceLanguage)}
                    </div>
                    <ProfileMenu initial={firstName[0].toUpperCase()} />
                </div>
            </div>

            <div className="home-level-strip">
                <div className="home-level-badge">{level}</div>
                <div className="home-level-bar-wrap">
                    <div className="home-level-bar-fill" style={{ width: `${levelPct}%` }}></div>
                </div>
                <div className="home-xp">⭐ {totalXp} XP</div>
                <div className="home-streak">🔥 {streak}</div>
            </div>

            {showLevelUp && (
                <div style={{
                    margin: '12px 16px 0',
                    padding: '16px',
                    borderRadius: 16,
                    background: '#E1F5EE',
                    border: '1px solid #9FE1CB',
                    color: '#085041',
                }}>
                    <div style={{ fontSize: 17, fontWeight: 700 }}>🎉 {lu('title')}</div>
                    <div style={{ fontSize: 14, marginTop: 4, lineHeight: 1.4 }}>{lu('body')}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button
                            onClick={handleLevelUp}
                            disabled={levelUpSaving}
                            style={{
                                flex: 1,
                                padding: '10px 14px',
                                borderRadius: 12,
                                border: 'none',
                                background: '#1D9E75',
                                color: '#FFFFFF',
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            {levelUpSaving ? lu('saving') : `${lu('up')} →`}
                        </button>
                        <button
                            onClick={dismissLevelUp}
                            style={{
                                padding: '10px 14px',
                                borderRadius: 12,
                                border: '1px solid #9FE1CB',
                                background: 'transparent',
                                color: '#085041',
                                fontSize: 14,
                                cursor: 'pointer',
                            }}
                        >
                            {lu('later')}
                        </button>
                    </div>
                </div>
            )}

            <div className="home-lang-tabs">
                {userLanguages.map(lang => (
                    <div
                        key={lang.target_language}
                        className={`home-lang-tab ${lang.target_language === activeLanguage ? 'active' : ''}`}
                        onClick={() => lang.target_language !== activeLanguage && switchLanguage(lang.target_language)}
                    >
                        {languageFlag(lang.target_language)} {languageLabel(lang.target_language, interfaceLanguage)}
                    </div>
                ))}
                <div className="home-lang-tab home-lang-add" onClick={() => navigate('/add-language')}>+ {t('home.addLanguage')}</div>
            </div>

            <div className="home-section">
                <div className="home-section-header">
                    <div className="home-section-label">{t('home.yourTopics')} · {level}</div>
                    <div className="home-see-all" onClick={() => navigate('/topics')}>{t('home.seeAll')} →</div>
                </div>

                <div className="home-topics-grid">
                    {topics.map(topic => (
                        <div
                            key={topic.slug}
                            className="home-topic-card"
                            onClick={() => navigate(`/topic/${topic.slug}`)}
                        >
                            <div className="home-topic-top">
                                <div className="home-topic-emoji">{topic.emoji}</div>
                            </div>
                            <div className="home-topic-name">{getTopicName(topic, interfaceLanguage)}</div>
                            <div className="home-topic-meta">
                                <div className={`home-topic-level ${topic.level === 'B2' ? 'b2' : 'b1'}`}>
                                    {topic.level}
                                </div>
                                <div className="home-topic-progress">{lessonsDone(topic.slug, completedByTopic)} / {lessonTotal(topic.slug)}</div>
                            </div>
                            <div className="home-topic-bar">
                                <div className="home-topic-bar-fill" style={{ width: `${Math.round((lessonsDone(topic.slug, completedByTopic) / lessonTotal(topic.slug)) * 100)}%` }}></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="home-luna-strip" onClick={() => navigate('/luna/everyday-life')}>
                <div className="home-luna-avatar">🦉</div>
                <div className="home-luna-text">
                    <div className="home-luna-name">{t('home.luna.title')}</div>
                    <div className="home-luna-hint">{t('home.luna.subtitle')}</div>
                </div>
                <button className="home-luna-btn" onClick={(e) => { e.stopPropagation(); navigate('/luna/everyday-life'); }}>{t('home.luna.cta')}</button>
            </div>

            <div className="home-section">
                <div className="home-section-header">
                    <div className="home-section-label">{t('home.quickPractice')}</div>
                </div>
                <div className="home-practice-grid">
                    <div className="home-practice-card" onClick={() => navigate('/vocabulary')} style={{ cursor: 'pointer' }}>
                        <div className="home-practice-icon">📖</div>
                        <div className="home-practice-name">{t('home.vocabulary')}</div>
                        <div className="home-practice-sub">{t('home.vocabularySub')}</div>
                    </div>
                    <div className="home-practice-card" onClick={() => navigate('/sentences')} style={{ cursor: 'pointer' }}>
                        <div className="home-practice-icon">✏️</div>
                        <div className="home-practice-name">{t('home.sentences')}</div>
                        <div className="home-practice-sub">{t('home.sentencesSub')}</div>
                    </div>
                    <div className="home-practice-card" onClick={() => navigate('/pronunciation')} style={{ cursor: 'pointer' }}>
                        <div className="home-practice-icon">🎙️</div>
                        <div className="home-practice-name">{t('home.pronunciation')}</div>
                        <div className="home-practice-sub">{t('home.pronunciationSub')}</div>
                    </div>
                    <div className="home-practice-card stories" onClick={() => navigate('/stories')} style={{ cursor: 'pointer' }}>
                        <div className="home-practice-icon">📚</div>
                        <div className="home-practice-name">{t('home.stories')}</div>
                        <div className="home-practice-sub">{t('home.storiesSub')}</div>
                    </div>
                </div>
            </div>

            <div style={{ height: '80px' }}></div>

            <div className="home-bottom-nav">
                <button className="home-nav-item active" onClick={() => navigate('/home')}>
                    <div className="home-nav-icon">🏠</div>
                    <div className="home-nav-label">{t('nav.home')}</div>
                    <div className="home-nav-dot"></div>
                </button>
                <button className="home-nav-item" onClick={() => navigate('/topics')}>
                    <div className="home-nav-icon">📚</div>
                    <div className="home-nav-label">{t('nav.topics')}</div>
                </button>
                <button className="home-nav-item" onClick={() => navigate('/grammar')}>
                    <div className="home-nav-icon nav-icon-grammar">📐</div>
                    <div className="home-nav-label">{t('nav.grammar')}</div>
                </button>
                <button className="home-nav-item" onClick={() => navigate('/wordbank')}>
                    <div className="home-nav-icon">📒</div>
                    <div className="home-nav-label">{t('nav.wordbank')}</div>
                </button>
                <button className="home-nav-item" onClick={() => navigate('/games')}>
                    <div className="home-nav-icon">🎮</div>
                    <div className="home-nav-label">{t('nav.games')}</div>
                </button>
            </div>

        </div>
    );
}

export default Home;